import React from 'react';
import styled from 'styled-components';
import { AudioProvider } from '../contexts/AudioContext';
import { AlbumSection, AlbumsHero } from '../components/audio';
import { FloatingPattern } from '../components/common';
import { theme } from '../theme';

const PageContainer = styled.div`
    position: relative;
    padding-top: 70px;
    overflow-x: hidden;
`;

// Album data
const garbageInGarbageOutTracks = [
    {
        title: [{ type: 'text' as const, content: 'Pentwater Saturday' }],
        artist: [{ type: 'text' as const, content: 'The Butterscotch Boys' }],
        album: [{ type: 'text' as const, content: 'Garbage In, Garbage Out' }],
        duration: '3:19',
        src: 'https://d110v70ry4kxfb.cloudfront.net/audio/garbage-in-garbage-out/01-pentwater-saturday.mp3',
    },
    {
        title: [{ type: 'text' as const, content: 'Fancy Town' }],
        artist: [{ type: 'text' as const, content: 'The Butterscotch Boys' }],
        album: [{ type: 'text' as const, content: 'Garbage In, Garbage Out' }],
        duration: '3:35',
        src: 'https://d110v70ry4kxfb.cloudfront.net/audio/garbage-in-garbage-out/02-fancy-town.mp3',
    },
    {
        title: [{ type: 'text' as const, content: 'Cheese Town' }],
        artist: [{ type: 'text' as const, content: 'The Butterscotch Boys' }],
        album: [{ type: 'text' as const, content: 'Garbage In, Garbage Out' }],
        duration: '4:11',
        src: 'https://d110v70ry4kxfb.cloudfront.net/audio/garbage-in-garbage-out/03-cheese-town.mp3',
    },
    {
        title: [{ type: 'text' as const, content: 'Lick Your Spoon' }],
        artist: [{ type: 'text' as const, content: 'The Butterscotch Boys' }],
        album: [{ type: 'text' as const, content: 'Garbage In, Garbage Out' }],
        duration: '4:35',
        src: 'https://d110v70ry4kxfb.cloudfront.net/audio/garbage-in-garbage-out/04-lick-your-spoon.mp3',
    },
    {
        title: [{ type: 'text' as const, content: 'Butterscotch Boy' }],
        artist: [{ type: 'text' as const, content: 'The Butterscotch Boys' }],
        album: [{ type: 'text' as const, content: 'Garbage In, Garbage Out' }],
        duration: '4:45',
        src: 'https://d110v70ry4kxfb.cloudfront.net/audio/garbage-in-garbage-out/05-butterscotch-boy.mp3',
    },
    {
        title: [{ type: 'text' as const, content: 'Those Days Are Gone' }],
        artist: [{ type: 'text' as const, content: 'The Butterscotch Boys' }],
        album: [{ type: 'text' as const, content: 'Garbage In, Garbage Out' }],
        duration: '2:27',
        src: 'https://d110v70ry4kxfb.cloudfront.net/audio/garbage-in-garbage-out/06-those-days-are-gone.mp3',
    },
    {
        title: [{ type: 'text' as const, content: 'Garbage In, Garbage Out' }],
        artist: [{ type: 'text' as const, content: 'The Butterscotch Boys' }],
        album: [{ type: 'text' as const, content: 'Garbage In, Garbage Out' }],
        duration: '7:26',
        src: 'https://d110v70ry4kxfb.cloudfront.net/audio/garbage-in-garbage-out/07-garbage-in-garbage-out.mp3',
    },
    {
        title: [{ type: 'text' as const, content: 'Old McDonald (had a wife)' }],
        artist: [{ type: 'text' as const, content: 'The Butterscotch Boys' }],
        album: [{ type: 'text' as const, content: 'Garbage In, Garbage Out' }],
        duration: '2:16',
        src: 'https://d110v70ry4kxfb.cloudfront.net/audio/garbage-in-garbage-out/08-old-mcdonald-had-a-wife.mp3',
    },
    {
        title: [{ type: 'text' as const, content: 'Trash Man' }],
        artist: [{ type: 'text' as const, content: 'The Butterscotch Boys' }],
        album: [{ type: 'text' as const, content: 'Garbage In, Garbage Out' }],
        duration: '4:05',
        src: 'https://d110v70ry4kxfb.cloudfront.net/audio/garbage-in-garbage-out/09-trash-man.mp3',
    },
];

const garbageInGarbageOutListInfo = {
    type: 'album' as const,
    name: 'Garbage In, Garbage Out',
    numTracks: 9,
    duration: '36:39',
    releaseDate: 'May 17, 2025',
    genre: 'Comedy Rock / Jazz Lounge',
    imageSrc: '/images/album-covers/garbage-in-garbage-out-album-cover-small.jpg',
};

const garbageInGarbageOutCredits = [
    { name: 'Tyler Vander Maas', role: 'Lead vocals, Songwriter' },
    { name: 'Adam Marth', role: 'Guitar' },
    { name: 'Dutcher Snedeker', role: 'Keys' },
    { name: 'Kevin Pritchard', role: 'Bass' },
    { name: 'Richard Hale', role: 'Drums' },
    { name: 'Ryan Hoger', role: 'Guitar, Album art' },
    { name: 'Kait Rose', role: 'Backing vocals' },
    { name: 'Aaron Stinson', role: 'Flute, Tenor sax' },
    { name: 'Sean VanHaren', role: 'Trombone, Sousaphone' },
    { name: 'Josh Kaufman', role: 'Mixing and mastering' },
    { name: 'Local Legend Recording', role: 'Recording studio' },
];

export const AudioPage: React.FC = () => {
    return (
        <AudioProvider>
            <PageContainer>
                <FloatingPattern />
                <AlbumsHero />

                {/* Album 1: Garbage In, Garbage Out */}
                <AlbumSection
                    albumId="garbage-in-garbage-out"
                    tracks={garbageInGarbageOutTracks}
                    listInfo={garbageInGarbageOutListInfo}
                    albumTitle="Garbage In, Garbage Out"
                    albumDescription="Recorded at Local Legend Recording, Grand Rapids, Michigan. All songs written by Tyler Vander Maas."
                    credits={garbageInGarbageOutCredits}
                    purchaseLink="https://thebutterscotchboys.bandcamp.com/album/garbage-in-garbage-out"
                    playlistPosition="left"
                    backgroundColor={theme.colors.backgroundLighter}
                />
            </PageContainer>
        </AudioProvider>
    );
};
